import { customizeError, formatDate } from '@chilecompra/react-kit'

import { CLAIM_TYPES, ERROR_REASONS } from '../../config/settings/constants'
import { makeActionCreator } from '../../config/store/utils'
import {
  fetchClaimReasons,
  fetchClaimTypes,
  fetchOrganizationByDni,
  fetchOrganizationByProcessCode,
  submitClaim
} from '../../services/claims'
import { onShowSnackbar } from '../SnackbarProvider'

const findResponseError = (error, errorCode) => {
  const errores = error?.originalError?.response?.data?.errores

  return Array.isArray(errores) ? errores.find(({ codigo }) => codigo === errorCode) : undefined
}

const resolveError = error => {
  const fileError = findResponseError(error, ERROR_REASONS.FILE_VALIDATION)
  const status = Number(error?.code ?? error?.originalError?.response?.status)

  if (status === 400 && fileError) return ERROR_REASONS.FILE_VALIDATION

  return ERROR_REASONS.SOMETHING_WENT_WRONG
}

const buildErrorPayload = error => ({
  error: {
    ...customizeError({
      code: error.code,
      originalError: error.originalError,
      reason: resolveError(error)
    }).toObject()
  }
})

const mapToSelectOptions = payload =>
  Array.isArray(payload) ? payload.map(({ id, name }) => ({ name, value: id })) : []

const mapFormToClaimPayload = claim => {
  const basePayload = {
    claimReasonId: claim.claimType === CLAIM_TYPES.LATE_PAYMENT ? 1 : claim.claimReason,
    claimTypeId: claim.claimType,
    processCode: claim.processCode,
    publicOrganizationName: claim.organizationName,
    publicOrganizationRut: claim.organizationDni,
    reason: claim.reason
  }

  if (claim.claimType === CLAIM_TYPES.LATE_PAYMENT) {
    return {
      ...basePayload,
      invoiceAmount: Number(claim.invoiceAmount),
      invoiceDeliveryDate: formatDate(claim.invoiceDate, 'yyyy-MM-dd'),
      invoiceNumber: claim.invoiceNumber,
      invoiceReceptionDate: formatDate(claim.acceptanceDate, 'yyyy-MM-dd')
    }
  }

  return basePayload
}

export const ADD_FILE_SUCCESS = 'ADD_FILE_SUCCESS'
export const onAddFileSuccess = makeActionCreator(ADD_FILE_SUCCESS, 'payload')
export const onAddFileThunk =
  ({ file }) =>
  async dispatch =>
    dispatch(onAddFileSuccess({ file, fileName: file.name }))

export const GET_CLAIM_TYPES = 'GET_CLAIM_TYPES'
export const GET_CLAIM_TYPES_ERROR = 'GET_CLAIM_TYPES_ERROR'
export const GET_CLAIM_TYPES_SUCCESS = 'GET_CLAIM_TYPES_SUCCESS'
export const onGetClaimTypes = makeActionCreator(GET_CLAIM_TYPES)
export const onGetClaimTypesError = makeActionCreator(GET_CLAIM_TYPES_ERROR, 'payload')
export const onGetClaimTypesSuccess = makeActionCreator(GET_CLAIM_TYPES_SUCCESS, 'payload')
export const onGetClaimTypesThunk = () => async dispatch => {
  dispatch(onGetClaimTypes())

  try {
    const { data } = await fetchClaimTypes()

    const claimTypes = mapToSelectOptions(data?.payload)

    return dispatch(onGetClaimTypesSuccess({ claimTypes }))
  } catch (error) {
    return dispatch(onGetClaimTypesError(buildErrorPayload(error)))
  }
}

export const GET_CLAIM_REASONS_BY_TYPE_ID = 'GET_CLAIM_REASONS_BY_TYPE_ID'
export const GET_CLAIM_REASONS_BY_TYPE_ID_ERROR = 'GET_CLAIM_REASONS_BY_TYPE_ID_ERROR'
export const GET_CLAIM_REASONS_BY_TYPE_ID_SUCCESS = 'GET_CLAIM_REASONS_BY_TYPE_ID_SUCCESS'
export const onGetClaimReasonsByTypeId = makeActionCreator(GET_CLAIM_REASONS_BY_TYPE_ID)
export const onGetClaimReasonsByTypeIdError = makeActionCreator(
  GET_CLAIM_REASONS_BY_TYPE_ID_ERROR,
  'payload'
)
export const onGetClaimReasonsByTypeIdSuccess = makeActionCreator(
  GET_CLAIM_REASONS_BY_TYPE_ID_SUCCESS,
  'payload'
)
export const onGetClaimReasonsByTypeIdThunk = () => async dispatch => {
  dispatch(onGetClaimReasonsByTypeId())

  try {
    const { data } = await fetchClaimReasons()

    const claimReasons = mapToSelectOptions(data?.payload)

    return dispatch(onGetClaimReasonsByTypeIdSuccess({ claimReasons }))
  } catch (error) {
    return dispatch(onGetClaimReasonsByTypeIdError(buildErrorPayload(error)))
  }
}

export const GET_ORGANIZATION_BY_PROCESS_CODE = 'GET_ORGANIZATION_BY_PROCESS_CODE'
export const GET_ORGANIZATION_BY_PROCESS_CODE_ERROR = 'GET_ORGANIZATION_BY_PROCESS_CODE_ERROR'
export const GET_ORGANIZATION_BY_PROCESS_CODE_SUCCESS = 'GET_ORGANIZATION_BY_PROCESS_CODE_SUCCESS'
export const onGetOrganizationByProcessCode = makeActionCreator(GET_ORGANIZATION_BY_PROCESS_CODE)
export const onGetOrganizationByProcessCodeError = makeActionCreator(
  GET_ORGANIZATION_BY_PROCESS_CODE_ERROR,
  'payload'
)
export const onGetOrganizationByProcessCodeSuccess = makeActionCreator(
  GET_ORGANIZATION_BY_PROCESS_CODE_SUCCESS,
  'payload'
)
export const onGetOrganizationByProcessCodeThunk =
  ({ processCode }) =>
  async dispatch => {
    dispatch(onGetOrganizationByProcessCode())

    try {
      const { data } = await fetchOrganizationByProcessCode({ processCode })

      const disabledIfFound = data?.payload?.found ?? true

      const organization = {
        dni: data?.payload?.publicOrganizationRut || '',
        name: data?.payload?.publicOrganizationName || ''
      }

      return dispatch(onGetOrganizationByProcessCodeSuccess({ disabledIfFound, organization }))
    } catch (error) {
      return dispatch(onGetOrganizationByProcessCodeError(buildErrorPayload(error)))
    }
  }

export const GET_ORGANIZATION_BY_DNI = 'GET_ORGANIZATION_BY_DNI'
export const GET_ORGANIZATION_BY_DNI_ERROR = 'GET_ORGANIZATION_BY_DNI_ERROR'
export const GET_ORGANIZATION_BY_DNI_SUCCESS = 'GET_ORGANIZATION_BY_DNI_SUCCESS'
export const onGetOrganizationByDni = makeActionCreator(GET_ORGANIZATION_BY_DNI)
export const onGetOrganizationByDniError = makeActionCreator(
  GET_ORGANIZATION_BY_DNI_ERROR,
  'payload'
)
export const onGetOrganizationByDniSuccess = makeActionCreator(
  GET_ORGANIZATION_BY_DNI_SUCCESS,
  'payload'
)
export const onGetOrganizationByDniThunk =
  ({ dni }) =>
  async dispatch => {
    dispatch(onGetOrganizationByDni())

    try {
      const { data } = await fetchOrganizationByDni({ dni })

      const organization = {
        dni,
        name: data?.payload?.publicOrganizationName || ''
      }

      return dispatch(onGetOrganizationByDniSuccess({ organization }))
    } catch (error) {
      return dispatch(onGetOrganizationByDniError(buildErrorPayload(error)))
    }
  }

export const CREATE_CLAIM = 'CREATE_CLAIM'
export const CREATE_CLAIM_ERROR = 'CREATE_CLAIM_ERROR'
export const CREATE_CLAIM_SUCCESS = 'CREATE_CLAIM_SUCCESS'
export const onCreateClaim = makeActionCreator(CREATE_CLAIM)
export const onCreateClaimError = makeActionCreator(CREATE_CLAIM_ERROR, 'payload')
export const onCreateClaimSuccess = makeActionCreator(CREATE_CLAIM_SUCCESS, 'payload')
export const onCreateClaimThunk =
  ({ attachedFiles, callbackFormReset, claim, ip }) =>
  async dispatch => {
    dispatch(onCreateClaim())

    try {
      const claimPayload = mapFormToClaimPayload(claim)

      const { data } = await submitClaim({
        attachedFiles,
        claim: {
          ...claimPayload,
          ip
        }
      })

      const code = data?.payload?.code ?? ''

      dispatch(
        onShowSnackbar({
          message: 'Tu reclamo fue ingresado correctamente.',
          severity: 'success'
        })
      )

      callbackFormReset()

      return dispatch(onCreateClaimSuccess({ code }))
    } catch (error) {
      dispatch(
        onShowSnackbar({
          message: 'No fue posible ingresar el reclamo. Por favor, inténtalo nuevamente.',
          severity: 'error'
        })
      )

      return dispatch(onCreateClaimError(buildErrorPayload(error)))
    }
  }

export const REMOVE_FILE_SUCCESS = 'REMOVE_FILE_SUCCESS'
export const onRemoveFileSuccess = makeActionCreator(REMOVE_FILE_SUCCESS, 'payload')

export const RESET_FORM_ERRORS = 'RESET_FORM_ERRORS'
export const onResetFormErrors = makeActionCreator(RESET_FORM_ERRORS)

export const RESET_ORGANIZATION = 'RESET_ORGANIZATION'
export const onResetOrganization = makeActionCreator(RESET_ORGANIZATION)

export const RESET_ORGANIZATION_ORG_NAME = 'RESET_ORGANIZATION_ORG_NAME'
export const onResetOrganizationOrgName = makeActionCreator(RESET_ORGANIZATION_ORG_NAME)
